import { motion } from "framer-motion";
import { MILESTONES, IMPACT_STATS } from "@/lib/data";
import StatsCounter from "@/components/StatsCounter";
import PageHero from "@/components/PageHero";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Shramik Group"
        title={<>Two decades of <em className="not-italic font-serif italic text-shramik-green">rural transformation</em>.</>}
        subtitle="A diversified enterprise nurtured by producers, powered by innovation, and dedicated to the communities that made us."
        image="https://images.unsplash.com/photo-1574943320219-553eb213f72d?auto=format&fit=crop&w=1200&q=80"
        testId="about-hero"
      />

      {/* STORY */}
      <section className="py-20 md:py-28" data-testid="about-story">
        <div className="container-page grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Our Story</div>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">Rooted in Sangamner. <em className="not-italic font-serif italic text-shramik-green">Growing across Maharashtra.</em></h2>
          </div>
          <div className="lg:col-span-7 text-lg text-slate-600 leading-relaxed space-y-5">
            <p>Shramik Group, founded and guided by the visionary leadership of Mr. Sahebrao Ramchandra Navale Patil, is a diversified and growth-driven enterprise operating across multiple sectors that play a vital role in rural and national development.</p>
            <p>Our core businesses span Agri-Inputs Trading, Milk Processing, Stone Mining, Building Materials, Fuel Retailing, Cattle Feed, Farming and Education. What began in 1976 as an "Agro Service Centre" grew into Shramik Krushi Seva Kendra — today one of the largest agricultural-input traders in the region.</p>
            <p>Our Education vertical (Seva Sanskar Sanstha) includes 4 colleges — B.Sc. Agriculture, B.Tech. Food Technology, B.Tech. Agricultural Engineering, Agricultural Polytechnic — plus two schools spanning kindergarten to higher secondary. Shramik Group remains committed to integrity, operational excellence and sustainable growth for farmers, customers and students.</p>
          </div>
        </div>
      </section>

      {/* VISION, MISSION, VALUES */}
      <section className="py-20 md:py-24 bg-shramik-greenLight/50" data-testid="vmv-section">
        <div className="container-page grid md:grid-cols-3 gap-6">
          {[
            ["Vision", "To be a premium 'Ingredients' supplier company — serving nutraceuticals, pediatric nutrition and high-end product manufacturers with residue-free, antibiotic-negative, aflatoxin-free dairy."],
            ["Mission", "Deliver quality agri-inputs, dairy products and infrastructure while empowering farmer families through direct procurement, education and welfare programs."],
            ["Core Values", "Ethical business. Optimal performance. Streamlined efficiency. Customer satisfaction. Farmer-first."],
          ].map(([t, d]) => (
            <div key={t} className="bg-white rounded-2xl p-8 border border-slate-100 card-hover">
              <div className="w-10 h-10 rounded-lg bg-shramik-green text-white flex items-center justify-center mb-6 font-display font-bold">{t[0]}</div>
              <h3 className="font-display text-2xl font-medium mb-3">{t}</h3>
              <p className="text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 md:py-24" data-testid="about-stats">
        <div className="container-page">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-10">
            {IMPACT_STATS.map((s, i) => <StatsCounter key={i} value={s.value} suffix={s.suffix} label={s.label} testId={`about-stat-${i}`} />)}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 md:py-32 relative" data-testid="milestones">
        <div className="container-page">
          <div className="overline mb-4">Milestones</div>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-16 text-balance max-w-3xl">Our <em className="not-italic font-serif italic text-shramik-green">growth journey</em>, at a glance.</h2>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-shramik-green/25"/>
            <div className="space-y-12">
              {MILESTONES.map((m, i) => (
                <motion.div key={m.y}
                  initial={{opacity:0, y:30}} whileInView={{opacity:1, y:0}} viewport={{once:true, margin:"-80px"}} transition={{duration:0.5}}
                  className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-16 ${i%2===1 ? "md:[&>*:first-child]:col-start-2" : ""}`}
                >
                  <div className={`${i%2===1 ? "md:pl-16 md:text-left" : "md:pr-16 md:text-right"}`}>
                    <div className="font-display text-5xl md:text-6xl font-light text-shramik-green leading-none">{m.y}</div>
                    <div className="mt-3 font-display font-medium text-xl md:text-2xl">{m.t}</div>
                    <div className="mt-3 text-slate-600 leading-relaxed max-w-md md:ml-auto">{m.d}</div>
                  </div>
                  <span className="absolute left-3 md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rounded-full bg-shramik-green ring-4 ring-white"/>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CHAIRMAN / FOUNDER */}
      <section className="py-24 md:py-32 bg-shramik-ink text-white overflow-hidden relative" data-testid="chairman-message">
        <div className="orb bg-shramik-green/25 w-[500px] h-[500px] -top-40 -right-20"/>
        <div className="container-page relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src="https://images.weserv.nl/?url=shramikgroup.in/img/sn.jpg&w=900&h=1125&fit=cover" alt="Founder & Chairman" className="w-full h-full object-cover"/>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-shramik-gold mb-4">Founder &amp; Chairman</div>
            <h3 className="font-display font-light text-3xl md:text-4xl leading-tight text-balance">
              Mr. Sahebrao Ramchandra <span className="font-serif italic text-shramik-gold">Navale Patil</span>
            </h3>
            <div className="mt-6 space-y-4 text-white/85 text-base md:text-lg leading-relaxed">
              <p>An Agricultural Science graduate born to an agricultural family, our Founder started the "Agro Service Centre" in 1976 — the seed from which today's Shramik Krushi Seva Kendra grew into one of the largest agri-input trading networks in the region.</p>
              <p>Under his leadership, Shramik Group expanded into Milk &amp; Food Processing (2015), Petroleum Retailing (2020), Education (through Seva Sanskar Sanstha) and Stone Mining (2024) — while never straying from ethical business and farmer-first values.</p>
              <p>Beyond business, he personally manages a rehabilitation and foster home for children — for the last 25 years. Over 50 children are in care today, and more than 2,500 past children have gone on to thrive in life.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
